import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CalendarX, Trash2, Loader2, Plus, AlertCircle } from 'lucide-react';
import { format, parseISO, isAfter } from 'date-fns';
import { BlockTimeModal } from './BlockTimeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AvailabilityOverridesList({ sessionToken, onUpdate }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);

  useEffect(() => {
    fetchBlocks();
  }, [sessionToken]);

  const fetchBlocks = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        `${BACKEND_URL}/api/v1/admin/availability/blocks`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setBlocks(data.blocks || []);
      } else {
        console.error('Failed to fetch blocks');
        setBlocks([]);
      }
    } catch (error) {
      console.error('Error fetching blocks:', error);
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      setDeleting(blockId);
      const response = await fetch(
        `${BACKEND_URL}/api/v1/admin/availability/unblock/${blockId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        }
      );

      if (response.ok) {
        setBlocks(prev => (prev || []).filter(b => b._id !== blockId));
        toast.success('Block removed');
        onUpdate?.();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to remove block');
    } finally {
      setDeleting(null);
    }
  };

  const handleBlockCreated = (response) => {
    // The API now returns { success, block_id, block, message }
    if (response?.block) {
      setBlocks(prev => [...prev, response.block].sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      ));
    }
    toast.success(response?.message || 'Time blocked successfully');
    onUpdate?.();
  };

  const upcomingBlocks = blocks.filter(b => {
    if (!b.date) return false;
    try {
      const blockDate = parseISO(b.date);
      return isAfter(blockDate, new Date()) || 
        format(blockDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    } catch {
      return false;
    }
  });

  return (
    <>
      <Card data-testid="availability-overrides-list">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CalendarX className="w-5 h-5 text-red-500" />
                Blocked Dates
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Dates and times when bookings are not available
              </p>
            </div>
            <Button 
              onClick={() => setShowBlockModal(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="add-block-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Block Time
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading blocks...</span>
            </div>
          ) : upcomingBlocks.length === 0 ? (
            <div className="text-center py-8">
              <CalendarX className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No blocked dates</p>
              <p className="text-sm text-gray-400 mt-1">
                Click "Block Time" to prevent bookings on specific dates
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {(upcomingBlocks || []).map((block) => (
                <div
                  key={block._id}
                  className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg"
                  data-testid={`block-item-${block._id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <CalendarX className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {format(parseISO(block.date), 'EEEE, MMMM d, yyyy')}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {block.start_time === '00:00' ? (
                          <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-200">
                            Full Day
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            {block.start_time} - {block.end_time || 'EOD'}
                          </Badge>
                        )}
                        <span className="text-sm text-gray-500">{block.reason}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteBlock(block._id)}
                    disabled={deleting === block._id}
                    className="text-red-600 hover:text-red-700 hover:bg-red-100"
                    data-testid={`delete-block-${block._id}`}
                  >
                    {deleting === block._id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Info Alert */}
          <div className="mt-6 p-3 bg-amber-50 border border-amber-100 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">How blocks work:</p>
              <ul className="mt-1 list-disc list-inside text-amber-700">
                <li>Blocked dates/times won't show as available to customers</li>
                <li>Existing bookings on blocked dates are NOT automatically cancelled</li>
                <li>Use blocks for holidays, vacation, or special events</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <BlockTimeModal
        isOpen={showBlockModal}
        onClose={() => setShowBlockModal(false)}
        sessionToken={sessionToken}
        onBlockCreated={handleBlockCreated}
      />
    </>
  );
}

export default AvailabilityOverridesList;
